import React, {useEffect, useRef, useState} from "react";
import {useNavigate, useOutletContext, useParams, useSearchParams} from "react-router-dom";
import Button from "../../../../../components/Button";
import api from "../../../../../lib/api";
import {notify} from "../../../../../components/Notifications";
import {getErrMsg} from "../../../../../lib/errors";
import {useBusiness} from "../../../../../context/BusinessContext";
import SectionHeading from "../../../../../components/SectionHeading";
import {MAX_UPLOAD_MB, MAX_UPLOAD_BYTES} from "../../../../../lib/uploads";

type OutletCtx = {tournamentId: string | null; isEdit?: boolean};

type Images = {imageUrl?: string | null; coverImageUrl?: string | null};

const cx = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(" ");

function ImageCard({
                       title,
                       subtitle,
                       url,
                       uploading,
                       onPick,
                       onRemove,
                       acceptHint,
                       variant,
                   }: {
    title: string;
    subtitle: string;
    url?: string | null;
    uploading: boolean;
    onPick: (file: File) => void;
    onRemove: () => void;
    acceptHint: string;
    variant: "listing" | "cover";
}) {
    const inputRef = useRef<HTMLInputElement>(null);
    const hasImage = !!url;

    const preview = (
        <div
            className={cx(
                "overflow-hidden rounded-xl border border-gray-200 bg-gray-50 ring-1 ring-black/5",
                variant === "listing" ? "w-full aspect-4/4" : "w-full max-h-120"
            )}
        >
            {hasImage ? (
                <img src={url!} alt={title} loading="lazy" decoding="async" className="h-full w-full object-cover"/>
            ) : (
                <div className="flex h-full items-center justify-center px-6 text-center text-sm text-gray-500">
                    No image uploaded yet
                </div>
            )}
        </div>
    );

    return (
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
                        <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-700">
                            {acceptHint}
                        </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600">{subtitle}</p>
                </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
                <div className="lg:col-span-7">
                    {preview}
                    <p className="mt-4 text-xs text-gray-500">
                        {variant === "listing"
                            ? "Preview shown as a square (1:1). This is how it appears in listings."
                            : "Preview shown as a banner. This is how it appears at the top of the tournament page."}
                    </p>
                </div>

                <div className="lg:col-span-5">
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                        <input
                            ref={inputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (!f) return;
                                onPick(f);
                                e.currentTarget.value = "";
                            }}
                        />

                        <div className="space-y-3">
                            <div className="text-sm text-gray-700">
                                <p className="font-medium">Tips</p>
                                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-600">
                                    <li>Use a high-quality image (sharp, not blurry).</li>
                                    <li>
                                        {variant === "listing"
                                            ? "Square images work best (800×800 recommended)."
                                            : "Wide banners work best (1200×300 or higher recommended)."}
                                    </li>
                                </ul>
                            </div>

                            <div className="flex flex-wrap items-center gap-3 pt-2">
                                <Button
                                    type="button"
                                    variant="primary"
                                    size="md"
                                    disabled={uploading}
                                    onClick={() => inputRef.current?.click()}
                                >
                                    {uploading ? "Uploading…" : hasImage ? "Change image" : "Upload image"}
                                </Button>

                                {hasImage ? (
                                    <button
                                        type="button"
                                        className="text-sm font-medium text-gray-700 hover:text-gray-900"
                                        disabled={uploading}
                                        onClick={onRemove}
                                    >
                                        Remove
                                    </button>
                                ) : null}
                            </div>

                            {uploading ? <div className="text-xs text-gray-500">Please wait while we upload…</div> : null}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export function ImagesStep({mode}: {mode: "create" | "edit"}) {
    const {activeBusiness} = useBusiness();
    const businessId = activeBusiness?.id ?? null;

    const navigate = useNavigate();
    const params = useParams();
    const [sp] = useSearchParams();
    const outlet = useOutletContext<OutletCtx>();

    const tournamentId =
        mode === "edit"
            ? (params.tournamentId ?? outlet.tournamentId ?? null)
            : (outlet.tournamentId ?? sp.get("tournamentId") ?? null);

    const [loading, setLoading] = useState(false);
    const [savingListing, setSavingListing] = useState(false);
    const [savingCover, setSavingCover] = useState(false);

    const [images, setImages] = useState<Images>({imageUrl: null, coverImageUrl: null});

    useEffect(() => {
        if (!businessId || !tournamentId) return;

        setLoading(true);
        (async () => {
            try {
                const {data} = await api.get(`/api/v2/business/${businessId}/tournaments/${tournamentId}`);
                setImages({imageUrl: data?.imageUrl ?? null, coverImageUrl: data?.coverImageUrl ?? null});
            } catch (e) {
                notify.error(getErrMsg(e, "Failed to load tournament images"));
            } finally {
                setLoading(false);
            }
        })();
    }, [businessId, tournamentId]);

    const upload = async (type: "listing" | "cover", file: File) => {
        if (!businessId || !tournamentId) return;

        if (file.size > MAX_UPLOAD_BYTES) {
            notify.error(`Image too large (max ${MAX_UPLOAD_MB}MB)`);
            return;
        }

        type === "listing" ? setSavingListing(true) : setSavingCover(true);

        try {
            const fd = new FormData();
            fd.append("file", file);

            const {data} = await api.patch(
                `/api/v2/business/${businessId}/tournaments/${tournamentId}/images?type=${type}`,
                fd,
                {headers: {"Content-Type": "multipart/form-data"}}
            );

            const url = data?.url || data?.imageUrl || data?.coverImageUrl || data;
            if (!url || typeof url !== "string") {
                notify.error("Upload succeeded, but API did not return an image URL.");
                return;
            }

            setImages((prev) => (type === "listing" ? {...prev, imageUrl: url} : {...prev, coverImageUrl: url}));
            notify.success(type === "listing" ? "Listing image uploaded" : "Cover image uploaded");
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to upload image"));
        } finally {
            type === "listing" ? setSavingListing(false) : setSavingCover(false);
        }
    };

    const remove = async (type: "listing" | "cover") => {
        if (!businessId || !tournamentId) return;

        type === "listing" ? setSavingListing(true) : setSavingCover(true);

        try {
            await api.delete(`/api/v2/business/${businessId}/tournaments/${tournamentId}/images?type=${type}`);
            setImages((prev) => (type === "listing" ? {...prev, imageUrl: null} : {...prev, coverImageUrl: null}));
            notify.success(type === "listing" ? "Listing image removed" : "Cover image removed");
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to remove image"));
        } finally {
            type === "listing" ? setSavingListing(false) : setSavingCover(false);
        }
    };

    const goNext = () => {
        if (!tournamentId) return;
        if (mode === "edit") navigate(`/business/tournaments/${encodeURIComponent(tournamentId)}/edit/play-categories`);
        else navigate(`/business/tournaments/new/play-categories?tournamentId=${encodeURIComponent(tournamentId)}`);
    };

    if (!tournamentId) {
        return (
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
                Please complete <span className="font-medium">Details</span> first.
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <SectionHeading
                title={mode === "edit" ? "Edit Tournament Images" : "Add Tournament Images"}
                description="Upload a listing image and an optional cover image for your tournament."
            />

            {loading ? (
                <div className="py-10 text-sm text-gray-500">Loading images…</div>
            ) : (
                <div className="space-y-6">
                    <ImageCard
                        title="Listing image"
                        subtitle="Used on the tournament grid/listing. Recommended: square (800×800)."
                        url={images.imageUrl}
                        uploading={savingListing}
                        onPick={(f) => upload("listing", f)}
                        onRemove={() => remove("listing")}
                        acceptHint="Square 1:1"
                        variant="listing"
                    />

                    <ImageCard
                        title="Cover image"
                        subtitle="Optional banner at the top of the tournament page."
                        url={images.coverImageUrl}
                        uploading={savingCover}
                        onPick={(f) => upload("cover", f)}
                        onRemove={() => remove("cover")}
                        acceptHint="Wide banner (1200×300+)"
                        variant="cover"
                    />
                </div>
            )}

            <div className="flex items-center justify-end gap-3">
                <Button type="button" variant="secondary" size="md" onClick={() => navigate("/business/tournaments")}>
                    Exit
                </Button>

                <Button
                    type="button"
                    variant="primary"
                    size="md"
                    disabled={savingListing || savingCover}
                    onClick={goNext}
                >
                    Continue to Play Categories
                </Button>
            </div>
        </div>
    );
}
